import {
  ROUTES,
  type AccountInfo,
  type Broadcast,
  type BulkUpsertRequest,
  type BulkUpsertResponse,
  type CacheStatusResponse,
  type CountResponse,
  type CreateBroadcastRequest,
  type CreateBroadcastResponse,
  type Draft,
  type GroupsResponse,
  type ImageUploadResponse,
  type MaterializeResponse,
  type MutationCountResponse,
  type NextBatchResponse,
  type ProgressStats,
  type PutDraftRequest,
  type RefreshBeginResponse,
  type RefreshCommitResponse,
  type ReportRequest,
  type ReportResponse,
  type Tag,
  type TargetFilter,
  type TargetsResponse,
} from "@tg/shared";

const TOKEN_KEY = "tg.appToken";

export function getAppToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? "";
}
export function setAppToken(t: string): void {
  localStorage.setItem(TOKEN_KEY, t);
}
export function clearAppToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(public status: number, public body: string) {
    super(`API ${status}: ${body}`);
  }
}

async function req<T>(path: string, init?: RequestInit & { json?: unknown }): Promise<T> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${getAppToken()}`,
    ...(init?.headers as Record<string, string> | undefined),
  };
  let body = init?.body;
  if (init?.json !== undefined) {
    headers["content-type"] = "application/json";
    body = JSON.stringify(init.json);
  }
  const res = await fetch(path, { ...init, headers, body });
  if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => ""));
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") ?? "";
  return (ct.includes("application/json") ? await res.json() : (await res.text())) as T;
}

// ---- cache / account ----
export const api = {
  cacheStatus: () => req<CacheStatusResponse>(ROUTES.cacheStatus),
  getAccount: () => req<AccountInfo>(ROUTES.account),
  putAccount: (info: AccountInfo) => req<{ ok: boolean }>(ROUTES.account, { method: "PUT", json: info }),

  // groups + refresh
  refreshBegin: () => req<RefreshBeginResponse>(ROUTES.groupsRefreshBegin, { method: "POST" }),
  bulkUpsert: (b: BulkUpsertRequest) =>
    req<BulkUpsertResponse>(ROUTES.groupsBulkUpsert, { method: "POST", json: b }),
  refreshCommit: (marker: number) =>
    req<RefreshCommitResponse>(ROUTES.groupsRefreshCommit, { method: "POST", json: { marker } }),
  groups: (qs: string) => req<GroupsResponse>(`${ROUTES.groups}${qs ? `?${qs}` : ""}`),
  groupsCount: (qs: string) => req<CountResponse>(`${ROUTES.groupsCount}${qs ? `?${qs}` : ""}`),

  // tags
  tags: () => req<Tag[]>(ROUTES.tags),
  createTag: (name: string, color?: string) =>
    req<Tag>(ROUTES.tags, { method: "POST", json: { name, color } }),
  updateTag: (id: number, patch: { name?: string; color?: string }) =>
    req<{ ok: boolean }>(ROUTES.tag(id), { method: "PATCH", json: patch }),
  deleteTag: (id: number) => req<{ ok: boolean }>(ROUTES.tag(id), { method: "DELETE" }),
  assignTag: (id: number, groupIds: string[]) =>
    req<MutationCountResponse>(ROUTES.tagAssign(id), { method: "POST", json: { groupIds } }),
  unassignTag: (id: number, groupIds: string[]) =>
    req<MutationCountResponse>(ROUTES.tagUnassign(id), { method: "POST", json: { groupIds } }),
  assignTagByFilter: (id: number, filter: TargetFilter) =>
    req<MutationCountResponse>(ROUTES.tagAssignByFilter(id), { method: "POST", json: { filter } }),

  // draft + image
  getDraft: () => req<Draft>(ROUTES.draft),
  putDraft: (patch: PutDraftRequest) => req<Draft>(ROUTES.draft, { method: "PUT", json: patch }),
  uploadImage: (bytes: ArrayBuffer | Blob, mime: string, w?: number, h?: number) => {
    const qs = new URLSearchParams();
    if (w) qs.set("w", String(w));
    if (h) qs.set("h", String(h));
    return req<ImageUploadResponse>(`${ROUTES.image}${qs.toString() ? `?${qs}` : ""}`, {
      method: "PUT",
      headers: { "content-type": mime },
      body: bytes,
    });
  },
  imageUrl: (key: string) => ROUTES.imageByKey(key),
  getImageBlob: async (key: string): Promise<Blob> => {
    const res = await fetch(ROUTES.imageByKey(key), {
      headers: { Authorization: `Bearer ${getAppToken()}` },
    });
    if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => ""));
    return res.blob();
  },

  // broadcasts
  createBroadcast: (b: CreateBroadcastRequest) =>
    req<CreateBroadcastResponse>(ROUTES.broadcasts, { method: "POST", json: b }),
  activeBroadcast: () => req<Broadcast | null>(ROUTES.broadcastActive),
  getBroadcast: (id: number) => req<Broadcast>(ROUTES.broadcast(id)),
  materialize: (id: number) =>
    req<MaterializeResponse>(ROUTES.broadcastMaterialize(id), { method: "POST" }),
  riskAck: (id: number) => req<{ ok: boolean }>(ROUTES.broadcastRiskAck(id), { method: "POST" }),
  startBroadcast: (id: number) => req<{ status: string }>(ROUTES.broadcastStart(id), { method: "POST" }),
  pauseBroadcast: (id: number) => req<{ status: string }>(ROUTES.broadcastPause(id), { method: "POST" }),
  resumeBroadcast: (id: number) => req<{ status: string }>(ROUTES.broadcastResume(id), { method: "POST" }),
  stopBroadcast: (id: number) => req<{ status: string }>(ROUTES.broadcastStop(id), { method: "POST" }),
  progress: (id: number) => req<ProgressStats>(ROUTES.broadcastProgress(id)),
  targets: (id: number, qs: string) =>
    req<TargetsResponse>(`${ROUTES.broadcastTargets(id)}${qs ? `?${qs}` : ""}`),
  nextBatch: (id: number, limit: number) =>
    req<NextBatchResponse>(ROUTES.broadcastNextBatch(id), { method: "POST", json: { limit } }),
  report: (id: number, r: ReportRequest) =>
    req<ReportResponse>(ROUTES.broadcastReport(id), { method: "POST", json: r }),
};

export type Api = typeof api;
